import { useState, useEffect } from "react";
import type { ActiveOrder } from "../types/game";

interface Props {
  orders: ActiveOrder[];
  onCancel: (orderId: string) => void;
}

export function OrdersPanel({ orders, onCancel }: Props) {
  // Force re-render every second for countdown updates
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const executing = orders.filter((o) => o.status === "executing");
  const completed = orders.filter((o) => o.status === "complete");
  const cancelled = orders.filter((o) => o.status === "cancelled");

  return (
    <div className="orders-panel">
      <div className="panel-header">ORDERS</div>
      <div className="orders-list">
        {executing.length === 0 && completed.length === 0 && cancelled.length === 0 && (
          <div className="orders-empty">No active orders</div>
        )}
        {executing.map((order) => {
          const elapsed = (Date.now() - order.startedAt) / 1000;
          const remaining = Math.max(0, order.totalDuration - elapsed);
          const progress = Math.min(elapsed / order.totalDuration, 1);

          return (
            <div key={order.id} className="order-item order-executing">
              <div className="order-header">
                <span className="order-label">{order.label}</span>
                <button
                  className="order-cancel"
                  onClick={() => onCancel(order.id)}
                  title="Cancel order"
                >
                  x
                </button>
              </div>
              <div className="order-progress-bar">
                <div
                  className="order-progress-fill"
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
              <div className="order-time">{formatTime(remaining)}</div>
            </div>
          );
        })}
        {completed.map((order) => (
          <div key={order.id} className="order-item order-complete">
            <span className="order-label">{order.label}</span>
            <span className="order-status-badge">COMPLETE</span>
          </div>
        ))}
        {cancelled.map((order) => (
          <div key={order.id} className="order-item order-cancelled">
            <span className="order-label">{order.label}</span>
            <span className="order-status-badge">CANCELLED</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}
